import React, { useEffect, useState } from 'react';
import { AdminLayout } from '../../components/AdminLayout';
import { motion } from 'framer-motion';
import { Save } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminAbout() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    subtitle: '',
    bio: '',
    mission: '',
    social_stats: {
      instagram: { followers: '', engagement: '' },
      youtube: { subscribers: '', views: '' },
      tiktok: { followers: '', likes: '' }
    },
    highlights: ['', '', '', '']
  });

  useEffect(() => {
    loadAboutData();
  }, []);

  const loadAboutData = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/public/about`);
      setFormData(response.data);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      const token = localStorage.getItem('token');
      const cleanData = {
        ...formData,
        highlights: formData.highlights.filter(h => h.trim() !== '')
      };
      
      await axios.put(`${BACKEND_URL}/api/admin/about`, cleanData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Conteúdo atualizado com sucesso!');
    } catch (error) {
      toast.error('Erro ao salvar conteúdo');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-screen">
          <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="mb-8">
            <h1 className="text-4xl font-bold mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Gerenciar Página Sobre
            </h1>
            <p className="text-muted-foreground text-lg">
              Edite o conteúdo da página institucional
            </p>
          </div>

          <form onSubmit={handleSubmit} className="max-w-4xl">
            <div className="bg-white rounded-2xl border border-border p-6 space-y-6">
              <div>
                <label className="block text-sm font-medium mb-2">Título</label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Sobre Beto Carvalho"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Subtítulo</label>
                <Input
                  value={formData.subtitle}
                  onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })}
                  placeholder="Apaixonado por Conhecimentos Gerais"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Biografia</label>
                <textarea
                  value={formData.bio}
                  onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-border bg-white min-h-[200px]"
                  placeholder="Conte sua história..."
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Missão</label>
                <textarea
                  value={formData.mission}
                  onChange={(e) => setFormData({ ...formData, mission: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-border bg-white min-h-[100px]"
                  placeholder="Qual é sua missão?"
                  required
                />
              </div>

              <div className="border-t pt-6">
                <h3 className="text-lg font-semibold mb-4">Estatísticas das Redes Sociais</h3>
                
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">Instagram - Seguidores</label>
                      <Input
                        value={formData.social_stats.instagram.followers}
                        onChange={(e) => setFormData({
                          ...formData,
                          social_stats: {
                            ...formData.social_stats,
                            instagram: { ...formData.social_stats.instagram, followers: e.target.value }
                          }
                        })}
                        placeholder="50K+"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">Instagram - Engajamento</label>
                      <Input
                        value={formData.social_stats.instagram.engagement}
                        onChange={(e) => setFormData({
                          ...formData,
                          social_stats: {
                            ...formData.social_stats,
                            instagram: { ...formData.social_stats.instagram, engagement: e.target.value }
                          }
                        })}
                        placeholder="Alto"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">YouTube - Inscritos</label>
                      <Input
                        value={formData.social_stats.youtube.subscribers}
                        onChange={(e) => setFormData({
                          ...formData,
                          social_stats: {
                            ...formData.social_stats,
                            youtube: { ...formData.social_stats.youtube, subscribers: e.target.value }
                          }
                        })}
                        placeholder="100K+"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">YouTube - Visualizações</label>
                      <Input
                        value={formData.social_stats.youtube.views}
                        onChange={(e) => setFormData({
                          ...formData,
                          social_stats: {
                            ...formData.social_stats,
                            youtube: { ...formData.social_stats.youtube, views: e.target.value }
                          }
                        })}
                        placeholder="5M+"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-2">TikTok - Seguidores</label>
                      <Input
                        value={formData.social_stats.tiktok.followers}
                        onChange={(e) => setFormData({
                          ...formData,
                          social_stats: {
                            ...formData.social_stats,
                            tiktok: { ...formData.social_stats.tiktok, followers: e.target.value }
                          }
                        })}
                        placeholder="80K+"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-2">TikTok - Curtidas</label>
                      <Input
                        value={formData.social_stats.tiktok.likes}
                        onChange={(e) => setFormData({
                          ...formData,
                          social_stats: {
                            ...formData.social_stats,
                            tiktok: { ...formData.social_stats.tiktok, likes: e.target.value }
                          }
                        })}
                        placeholder="2M+"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="border-t pt-6">
                <h3 className="text-lg font-semibold mb-4">Destaques</h3>
                <div className="mb-3">
                  <Input
                    value={formData.highlights[0] || ''}
                    onChange={(e) => {
                      const newHighlights = [...formData.highlights];
                      newHighlights[0] = e.target.value;
                      setFormData({ ...formData, highlights: newHighlights });
                    }}
                    placeholder="Destaque 1"
                  />
                </div>
                <div className="mb-3">
                  <Input
                    value={formData.highlights[1] || ''}
                    onChange={(e) => {
                      const newHighlights = [...formData.highlights];
                      newHighlights[1] = e.target.value;
                      setFormData({ ...formData, highlights: newHighlights });
                    }}
                    placeholder="Destaque 2"
                  />
                </div>
                <div className="mb-3">
                  <Input
                    value={formData.highlights[2] || ''}
                    onChange={(e) => {
                      const newHighlights = [...formData.highlights];
                      newHighlights[2] = e.target.value;
                      setFormData({ ...formData, highlights: newHighlights });
                    }}
                    placeholder="Destaque 3"
                  />
                </div>
                <div className="mb-3">
                  <Input
                    value={formData.highlights[3] || ''}
                    onChange={(e) => {
                      const newHighlights = [...formData.highlights];
                      newHighlights[3] = e.target.value;
                      setFormData({ ...formData, highlights: newHighlights });
                    }}
                    placeholder="Destaque 4"
                  />
                </div>
              </div>

              <div className="flex justify-end pt-4">
                <Button
                  type="submit"
                  disabled={saving}
                  className="bg-gradient-to-r from-orange-500 to-red-500 px-8"
                >
                  <Save className="h-4 w-4 mr-2" />
                  {saving ? 'Salvando...' : 'Salvar Alterações'}
                </Button>
              </div>
            </div>
          </form>
        </motion.div>
      </div>
    </AdminLayout>
  );
}
